import { useState, useEffect } from "react";
import {
  feedbackBatchesArchiveFeedbackBatch,
  feedbackBatchesGetFeedbackBatch,
  feedbackBatchesProcessFeedbackBatch,
  feedbackBatchesPromoteFeedbackBatch,
  feedbackBatchesRejectFeedbackBatch,
  feedbackBatchesReopenFeedbackBatch,
  feedbackBatchesRemoveCommentFromBatch,
  feedbackBatchesRemoveEvaluationFromBatch,
  rulesListRules,
  rulesGetRule,
  rulesUpdateRule,
  knowledgeUpdateKnowledgeItem,
  guardrailsUpdateItem,
} from "../../gen";
import type { BatchDetailResponse, RuleResponse } from "../../gen/models";
import { useToast } from "../../context/ToastContext";
import { extractListItems } from "../thread/threadUtils";

export function useBatchDetail(batchId?: string, onBatchUpdated?: () => void) {
  const { toast } = useToast();

  const [batch, setBatch] = useState<BatchDetailResponse | null>(null);
  const [baselineRules, setBaselineRules] = useState<Map<string, RuleResponse>>(new Map());
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);

  // Selective approval toggles
  const [excludedRuleIds, setExcludedRuleIds] = useState<Set<string>>(new Set());
  const [excludedKnowledgeIds, setExcludedKnowledgeIds] = useState<Set<string>>(new Set());
  const [excludedGuardrailIds, setExcludedGuardrailIds] = useState<Set<string>>(new Set());

  const fetchBaselineRules = async (draftRules: any[]) => {
    const supersededIds = (draftRules || [])
      .map((r: any) => r.supersedes_rule_id)
      .filter(Boolean) as string[];

    if (supersededIds.length === 0) {
      setBaselineRules(new Map());
      return;
    }

    try {
      const rulesRes = await rulesListRules({ limit: 200 });
      const all = extractListItems<RuleResponse>(rulesRes);
      const map = new Map<string, RuleResponse>();
      for (const r of all) {
        map.set(r.id, r);
      }

      const missingIds = supersededIds.filter((id) => !map.has(id));
      if (missingIds.length > 0) {
        await Promise.all(
          missingIds.map(async (id) => {
            try {
              const singleRes = await rulesGetRule(id);
              const single = (singleRes as any).data as RuleResponse;
              if (single && single.id) {
                map.set(single.id, single);
              }
            } catch {
              // ignore
            }
          })
        );
      }

      setBaselineRules(map);
    } catch (err: any) {
      console.error("Failed to load baseline rules:", err);
    }
  };

  const loadBatchDetails = async () => {
    if (!batchId) return;
    try {
      const res = await feedbackBatchesGetFeedbackBatch(batchId);
      const bData = (res as any).data as BatchDetailResponse;
      setBatch(bData);
      await fetchBaselineRules(bData.draft_rules || []);
    } catch (err: any) {
      console.error("Failed to load batch details:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!batchId) return;
    setLoading(true);
    setExcludedRuleIds(new Set());
    setExcludedKnowledgeIds(new Set());
    setExcludedGuardrailIds(new Set());
    loadBatchDetails();
  }, [batchId]);

  // Polling effect while batch is in processing state
  useEffect(() => {
    if (!batch || batch.status !== "processing") return;

    let isPolling = false;
    const interval = setInterval(async () => {
      if (isPolling) return;
      isPolling = true;
      try {
        const res = await feedbackBatchesGetFeedbackBatch(batch.id);
        const updated = (res as any).data as BatchDetailResponse;
        if (updated.draft_rules && updated.draft_rules.length > 0) {
          await fetchBaselineRules(updated.draft_rules);
        }
        setBatch(updated);
        if (updated.status !== "processing") {
          clearInterval(interval);
          if (updated.status === "failed_processing") {
            toast.error(
              updated.error_message
                ? `Batch processing failed: ${updated.error_message}`
                : "Batch processing failed."
            );
          } else if (updated.status === "review_ready") {
            toast.success("Batch processing complete. Ready for review.");
          }
          if (onBatchUpdated) onBatchUpdated();
        }
      } catch {
        // ignore polling errors
      } finally {
        isPolling = false;
      }
    }, 3000);

    return () => clearInterval(interval);
  }, [batch?.id, batch?.status]);

  const handleProcessBatch = async () => {
    if (!batch) return;
    setActionLoading(true);
    try {
      await feedbackBatchesProcessFeedbackBatch(batch.id);
      setBatch((prev) => (prev ? { ...prev, status: "processing" } : null));
      if (onBatchUpdated) onBatchUpdated();
      toast.info("Batch processing started.");
    } catch (err: any) {
      toast.error("Failed to process batch: " + (err?.message || "Unknown error"));
    } finally {
      setActionLoading(false);
    }
  };

  const handlePromoteBatch = async () => {
    if (!batch) return;
    setActionLoading(true);
    try {
      await feedbackBatchesPromoteFeedbackBatch(batch.id);
      await loadBatchDetails();
      if (onBatchUpdated) onBatchUpdated();
      toast.success("Batch successfully promoted to production.");
    } catch (err: any) {
      toast.error("Failed to promote batch: " + (err?.message || "Unknown error"));
    } finally {
      setActionLoading(false);
    }
  };

  const handleRejectBatch = async () => {
    if (!batch) return;
    setActionLoading(true);
    try {
      await feedbackBatchesRejectFeedbackBatch(batch.id);
      await loadBatchDetails();
      if (onBatchUpdated) onBatchUpdated();
      toast.info("Batch has been rejected.");
    } catch (err: any) {
      toast.error("Failed to reject batch: " + (err?.message || "Unknown error"));
    } finally {
      setActionLoading(false);
    }
  };

  const handleArchiveBatch = async () => {
    if (!batch) return;
    setActionLoading(true);
    try {
      await feedbackBatchesArchiveFeedbackBatch(batch.id);
      await loadBatchDetails();
      if (onBatchUpdated) onBatchUpdated();
      toast.info("Batch has been archived.");
    } catch (err: any) {
      toast.error("Failed to archive batch: " + (err?.message || "Unknown error"));
    } finally {
      setActionLoading(false);
    }
  };

  const handleReopenBatch = async () => {
    if (!batch) return;
    setActionLoading(true);
    try {
      await feedbackBatchesReopenFeedbackBatch(batch.id);
      await loadBatchDetails();
      if (onBatchUpdated) onBatchUpdated();
      toast.success("Batch returned to collecting mode. Draft changesets deleted.");
    } catch (err: any) {
      toast.error("Failed to reset batch: " + (err?.message || "Unknown error"));
    } finally {
      setActionLoading(false);
    }
  };

  const handleRemoveComment = async (commentId: string) => {
    if (!batch) return;
    try {
      await feedbackBatchesRemoveCommentFromBatch(batch.id, commentId);
      setBatch((prev) =>
        prev ? { ...prev, comments: prev.comments.filter((c: any) => c.id !== commentId) } : null
      );
      if (onBatchUpdated) onBatchUpdated();
      toast.info("Comment removed from batch.");
    } catch (err: any) {
      toast.error("Failed to remove comment: " + (err?.message || "Unknown error"));
    }
  };

  const handleRemoveEvaluation = async (evaluationId: string) => {
    if (!batch) return;
    try {
      await feedbackBatchesRemoveEvaluationFromBatch(batch.id, evaluationId);
      setBatch((prev) =>
        prev
          ? {
              ...prev,
              evaluations: prev.evaluations.filter((e: any) => e.id !== evaluationId),
            }
          : null
      );
      if (onBatchUpdated) onBatchUpdated();
      toast.info("Evaluation removed from batch.");
    } catch (err: any) {
      toast.error("Failed to remove evaluation: " + (err?.message || "Unknown error"));
    }
  };

  const handleSaveRuleInPlace = async (
    ruleId: string,
    updates: { title: string; instruction: string; priority: number }
  ) => {
    try {
      await rulesUpdateRule(ruleId, updates);
      setBatch((prev) => {
        if (!prev) return null;
        return {
          ...prev,
          draft_rules: (prev.draft_rules || []).map((r: any) =>
            r.id === ruleId ? { ...r, ...updates } : r
          ),
        };
      });
      toast.success("Rule draft updated.");
    } catch (err: any) {
      toast.error("Failed to update rule: " + (err?.message || "Unknown error"));
      throw err;
    }
  };

  const handleSaveKnowledgeInPlace = async (
    itemId: string,
    updates: { title: string; content: string }
  ) => {
    try {
      await knowledgeUpdateKnowledgeItem(itemId, updates);
      setBatch((prev) => {
        if (!prev) return null;
        return {
          ...prev,
          draft_knowledge_items: (prev.draft_knowledge_items || []).map((k: any) =>
            k.id === itemId ? { ...k, ...updates } : k
          ),
        };
      });
      toast.success("Knowledge draft updated.");
    } catch (err: any) {
      toast.error("Failed to update knowledge: " + (err?.message || "Unknown error"));
      throw err;
    }
  };

  const handleSaveGuardrailInPlace = async (
    itemId: string,
    updates: {
      title: string;
      description?: string;
      category: string;
      action: string;
      severity: string;
      default_threshold: number;
      anchors?: Array<{ anchor_type: string; text: string }>;
    }
  ) => {
    try {
      await guardrailsUpdateItem(itemId, updates as any);
      setBatch((prev) => {
        if (!prev) return null;
        return {
          ...prev,
          draft_guardrails: (prev.draft_guardrails || []).map((g: any) =>
            g.id === itemId ? { ...g, ...updates } : g
          ),
        };
      });
      toast.success("Guardrail draft updated.");
    } catch (err: any) {
      toast.error("Failed to update guardrail: " + (err?.message || "Unknown error"));
      throw err;
    }
  };

  const toggleExcludeRule = (ruleId: string) => {
    setExcludedRuleIds((prev) => {
      const next = new Set(prev);
      if (next.has(ruleId)) next.delete(ruleId);
      else next.add(ruleId);
      return next;
    });
  };

  const toggleExcludeKnowledge = (kId: string) => {
    setExcludedKnowledgeIds((prev) => {
      const next = new Set(prev);
      if (next.has(kId)) next.delete(kId);
      else next.add(kId);
      return next;
    });
  };

  const toggleExcludeGuardrail = (guardrailId: string) => {
    setExcludedGuardrailIds((prev) => {
      const next = new Set(prev);
      if (next.has(guardrailId)) next.delete(guardrailId);
      else next.add(guardrailId);
      return next;
    });
  };

  return {
    batch,
    setBatch,
    baselineRules,
    loading,
    actionLoading,
    excludedRuleIds,
    excludedKnowledgeIds,
    excludedGuardrailIds,
    toggleExcludeRule,
    toggleExcludeKnowledge,
    toggleExcludeGuardrail,
    loadBatchDetails,
    handleProcessBatch,
    handlePromoteBatch,
    handleRejectBatch,
    handleArchiveBatch,
    handleReopenBatch,
    handleRemoveComment,
    handleRemoveEvaluation,
    handleSaveRuleInPlace,
    handleSaveKnowledgeInPlace,
    handleSaveGuardrailInPlace,
  };
}
